import { Injectable, signal, effect, inject, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Web3Service } from './web3.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UserProfile {
  id: string;
  walletAddress: string;
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private web3Service = inject(Web3Service);

  jwtToken = signal<string | null>(null);
  refreshToken = signal<string | null>(null);
  currentUser = signal<UserProfile | null>(null);
  isAuthenticated = signal<boolean>(false);

  promptForLoginOnConnect = true;

  constructor() {
    const storedToken = localStorage.getItem('jwtToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedUser = localStorage.getItem('currentUser');

    if (storedToken && storedUser) {
      this.jwtToken.set(storedToken);
      if (storedRefreshToken) this.refreshToken.set(storedRefreshToken);
      this.currentUser.set(JSON.parse(storedUser));
      this.isAuthenticated.set(true);
    }

    effect(() => {
      const token = this.jwtToken();
      const rToken = this.refreshToken();
      const user = this.currentUser();

      if (token && user) {
        localStorage.setItem('jwtToken', token);
        if (rToken) localStorage.setItem('refreshToken', rToken);
        localStorage.setItem('currentUser', JSON.stringify(user));
      } else {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('currentUser');
      }
    });

    effect(() => {
      const activeAddress = this.web3Service.address();

      untracked(() => {
        const user = this.currentUser();

        if (activeAddress && (!user || activeAddress.toLowerCase() !== user.walletAddress.toLowerCase())) {
          if (user) {
            console.warn('Wallet address changed! Logging out old session and disconnecting.');
            this.logout();
            this.web3Service.disconnect();
            return;
          }

          if (this.promptForLoginOnConnect) {
            this.promptForLoginOnConnect = false;
            this.login(activeAddress).catch(err => {
              console.error('Signature prompt rejected or failed:', err);
              // We no longer force a web3 disconnect here. Users can still swap on-chain without backend auth!
            });
          }
        }
      });
    }, { allowSignalWrites: true });
  }

  async login(walletAddress: string): Promise<void> {
    try {
      const nonceRes: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/auth/nonce/${walletAddress}`)
      );

      if (!nonceRes.status) {
        throw new Error(nonceRes.message || 'Failed to get nonce');
      }

      const nonce = nonceRes.data.nonce;
      const messageToSign = `Welcome to DEX!\n\nPlease sign this message to verify your identity and connect your wallet.\n\nNonce: ${nonce}`;
      const signature = await this.web3Service.signMessage(messageToSign);

      const loginRes: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/user/auth/login`, {
          walletAddress,
          signature
        })
      );

      if (!loginRes.status) {
        throw new Error(loginRes.message || 'Login failed');
      }

      this.jwtToken.set(loginRes.result.token);
      this.refreshToken.set(loginRes.result.refreshToken);
      this.currentUser.set(loginRes.result.data);
      this.isAuthenticated.set(true);

      console.log('Web3 Login Successful!');
    } catch (error) {
      console.error('Web3 Login Error:', error);
      this.logout();
      throw error;
    }
  }

  logout(): void {
    this.jwtToken.set(null);
    this.refreshToken.set(null);
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
  }

  async refreshTokens(): Promise<boolean> {
    const rToken = this.refreshToken();
    if (!rToken) return false;

    try {
      const res: any = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/user/auth/refresh`, {
          refreshToken: rToken
        })
      );
      if (res.status && res.result) {
        this.jwtToken.set(res.result.token);
        this.refreshToken.set(res.result.refreshToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to refresh token', error);
      this.logout();
      return false;
    }
  }
}
